import {
  AuthResponse,
  LoginCredentials,
  SignupData,
  RegisterPayload,
  LoginResponse,
  TwoFaVerifyRequest,
  TwoFaVerifyResponse,
  TwoFaResendResponse,
  TwoFaSetupRequest,
  TwoFaSetupResponse,
  TwoFaVerifyTotpSetupRequest,
  TwoFaSetupTotpVerifiedResponse,
  TwoFaStatusResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
  ValidateTokenResponse,
} from '../types/auth.types';
import { apiClient } from '../../../services/api';

export const authApi = {
  // Login actualizado para manejar 2FA
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: SignupData): Promise<AuthResponse> => {
    // Transformar los datos del formulario al formato que espera la API
    const registerPayload: RegisterPayload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      nationalId: userData.nationalId,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      confirmPassword: userData.confirmPassword,
      organizationName: userData.organizationName,
      organizationTaxId: userData.organizationTaxId,
      invitationCode: userData.invitationCode,
      profileId: 3, // Valor fijo según requerimiento
      planId: 1,    // Valor fijo según requerimiento
      passwordConfirmed: true,
      acceptedTerms: userData.acceptedTerms,
    };

    const response = await apiClient.post('/auth/register', registerPayload);
    return response.data;
  },

  // Mantener signup para retrocompatibilidad (deprecated)
  signup: async (userData: SignupData): Promise<AuthResponse> => {
    return this.register(userData);
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  verifyToken: async (): Promise<boolean> => {
    try {
      await apiClient.get('/auth/validate');
      return true;
    } catch {
      return false;
    }
  },

  // Nuevo método para reenviar email de verificación
  resendVerificationEmail: async (email: string): Promise<void> => {
    await apiClient.post('/auth/resend-verification', { email });
  },

  // Verificar email con token
  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token });
  },

  // ============================================
  // 2FA Methods
  // ============================================

  // Verificar código 2FA durante login
  verifyTwoFa: async (data: TwoFaVerifyRequest): Promise<TwoFaVerifyResponse> => {
    const payload = {
      temporaryToken: data.challengeId,
      code: data.code
    };
    const response = await apiClient.post('/auth/2fa/verify', payload);
    return response.data;
  },

  // Reenviar código 2FA (solo para EMAIL)
  resendTwoFaCode: async (challengeId: string): Promise<TwoFaResendResponse> => {
    const response = await apiClient.post('/auth/2fa/resend-email-code', {
      temporaryToken: challengeId,
    });
    return response.data;
  },

  // Configurar 2FA (EMAIL o TOTP)
  setupTwoFa: async (data: TwoFaSetupRequest): Promise<TwoFaSetupResponse> => {
    // Si es TOTP, llamamos al endpoint específico para obtener QR
    if (data.method === 'TOTP') {
      const response = await apiClient.post('/auth/2fa/setup-totp');
      return response.data;
    }
    // Si es EMAIL, llamamos a enable directamente
    else {
      const response = await apiClient.post('/auth/2fa/enable', data);
      return response.data;
    }
  },

  // Verificar código TOTP durante configuración
  verifyTotpSetup: async (
    data: TwoFaVerifyTotpSetupRequest
  ): Promise<TwoFaSetupTotpVerifiedResponse> => {
    const response = await apiClient.post('/auth/2fa/verify-setup', data);
    return response.data;
  },

  // Obtener estado de 2FA
  getTwoFaStatus: async (): Promise<TwoFaStatusResponse> => {
    const response = await apiClient.get('/auth/2fa/status');
    return response.data;
  },

  // Desactivar 2FA
  disableTwoFa: async (): Promise<void> => {
    await apiClient.post('/auth/2fa/disable');
  },

  // Regenerar códigos de respaldo
  regenerateBackupCodes: async (): Promise<{ backupCodes: string[] }> => {
    const response = await apiClient.post('/auth/2fa/backup-codes/regenerate');
    return response.data;
  },

  // ============================================
  // Password Reset
  // ============================================

  // Solicitar recuperación de contraseña
  forgotPassword: async (data: ForgotPasswordRequest): Promise<PasswordResetResponse> => {
    const response = await apiClient.post('/auth/forgot-password', data);
    return response.data;
  },

  // Restablecer contraseña con token
  resetPassword: async (data: ResetPasswordRequest): Promise<PasswordResetResponse> => {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  },

  // Validar token de recuperación
  validateResetToken: async (token: string): Promise<ValidateTokenResponse> => {
    const response = await apiClient.get('/auth/validate-reset-token', {
      params: { token }
    });
    return response.data;
  },
};
